import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './TopSitesCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import IFTable from 'Components/IFTable/IFTable'
import ReportIcon from '@mui/icons-material/Report'
import GoogleMapReact from 'google-map-react'
import IFsvg from 'Components/IFsvg/IFsvg'
import { Config } from 'Config'
import { getEnergyUnit } from 'Utils/Calculations'

const TopSitesCard = ({
  fetchTopSites,
  topSites,
  filter,
  fetchTopSitesRequestState,
}) => {
  const { t } = useTranslation()
  const rowRefs = useRef({})
  const [hasAutoFitted, setHasAutoFitted] = useState(false)
  const tableRef = useRef(null)
  const selectedCountry = filter?.find((f) => f.field === 'Country')?.value
  const COUNTRY_CENTERS = {
    Egypt: { lat: 30.0444, lng: 31.2357 }, // Cairo
    Jordan: { lat: 31.9539, lng: 35.9106 }, // Amman
  }
  const defaultCenter =
    COUNTRY_CENTERS[selectedCountry] || COUNTRY_CENTERS.Egypt
  useEffect(() => {
    fetchTopSites(filter)
    setHasAutoFitted(false)
  }, [filter])

  const [selectedSite, setSelectedSite] = useState(null)
  useEffect(() => {
    if (topSites?.data?.length > 0) {
      setSelectedSite(topSites.data[0])

      tableRef.current?.setSelectedRowId(
        topSites.data.findIndex(
          (s) => s.stationName === topSites.data[0].stationName,
        ),
      )
    }
  }, [topSites])
  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTopSitesRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTopSitesRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTopSitesRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTopSitesRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTopSitesRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTopSitesRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchTopSitesRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchTopSitesRequestState])

  const topSitesData = Array.isArray(topSites?.data) ? topSites.data : []
  const maxEnergyConsumption = Math.max(
    ...topSitesData.map((item) => item.energyConsumption || 0),
    0,
  )

  const energyUnitConfig = getEnergyUnit(maxEnergyConsumption)
  const TopSitesTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 50,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
          },
        },
        header:
          fetchTopSitesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.StationName')}
            </div>
          ),
        Cell: ({ row }) => {
          return row.index < topSites?.data?.length ? (
            <div ref={(el) => (rowRefs.current[row.original.stationName] = el)}>
              <IFText>{row.original.stationName}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'transactions',
        enableEditing: false,
        size: 50,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopSitesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.NumberOfTransactions')}
            </div>
          ),
        Cell: ({ row }) => {
          return row.index < topSites?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.transactionsCount}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'energyConsumption',
        enableEditing: false,
        size: 68,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopSitesRequestState === RequestState.LOADING ? (
            <IFSkeleton
              variant="text"
              width="140px"
              animation="none"
              height={20}
            />
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.EnergyConsumption')} ({energyUnitConfig.unit})
            </div>
          ),

        Cell: ({ row }) => {
          if (row.index < topSites?.data?.length) {
            const rawValue = row.original.energyConsumption ?? 0

            const formattedValue = (
              rawValue / energyUnitConfig.divisor
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })

            return (
              <div className={Styles.Header}>
                <IFText>{formattedValue}</IFText>
              </div>
            )
          }

          if (failedRequest) {
            return (
              <div>
                <ReportIcon
                  width={28}
                  height={28}
                  style={{ color: Colors.red }}
                />
                <IFText>{t('IFTable.TryAgainText')}</IFText>
              </div>
            )
          }

          return (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'utilization',
        enableEditing: false,
        size: 16,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopSitesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.Utilization')}
            </div>
          ),
        Cell: ({ row }) => {
          return row.index < topSites?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>
                {row.original.utilization
                  ? `${row.original.utilization}%`
                  : '-'}
              </IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'repeatedVisits',
        enableEditing: false,
        size: 16,
        muiTableHeadCellProps: {
          align: 'center',

          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopSitesRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.RepeatedVisits')}
            </div>
          ),
        Cell: ({ row }) => {
          return row.index < topSites?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.repeatedVisits}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [topSites, fetchTopSites, fetchTopSitesRequestState, failedRequest],
  )
  const loadMoreData = () => {
    if (fetchTopSitesRequestState === RequestState.LOADING) return
    fetchTopSites(filter)
  }
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const mapOptions = {
    fullscreenControl: false,
    maxZoom: 20,
    clickableIcons: false,
    styles: [
      { elementType: 'geometry', stylers: [{ color: Colors.white }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      {
        elementType: 'labels.text.fill',
        stylers: [{ color: Colors.GoogleMaps.TextFill }],
      },
      { elementType: 'labels.text.stroke', stylers: [{ color: Colors.white }] },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: Colors.CardShadow }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: Colors.GoogleMaps.Highway }],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: Colors.GoogleMaps.Water }],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: Colors.GoogleMaps.Park }],
      },
    ],
  }

  const Marker = ({ children, onClick, style }) => (
    <div
      onClick={onClick}
      style={{
        transform: 'translate(-50%, -100%)',
        fontSize: '24px',
        color: Colors.primary,
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </div>
  )

  const mapRef = useRef(null)
  const mapsRef = useRef(null)
  const firstFitDoneRef = useRef(false)

  const validSites = (topSitesData || []).filter(
    (s) =>
      s &&
      s.latitude !== undefined &&
      s.longitude !== undefined &&
      !Number.isNaN(parseFloat(s.latitude)) &&
      !Number.isNaN(parseFloat(s.longitude)),
  )

  const fitBoundsIfPossible = (map, maps) => {
    if (!map || !maps) return
    if (!validSites || validSites.length === 0) return

    try {
      const bounds = new maps.LatLngBounds()
      validSites.forEach((site) => {
        bounds.extend({
          lat: parseFloat(site.latitude),
          lng: parseFloat(site.longitude),
        })
      })
      map.fitBounds(bounds)
    } catch (err) {}
  }
  useEffect(() => {
    if (!mapRef.current || !mapsRef.current) return

    if (selectedSite && selectedSite.latitude && selectedSite.longitude) {
      const lat = parseFloat(selectedSite.latitude)
      const lng = parseFloat(selectedSite.longitude)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        try {
          mapRef.current.panTo({ lat, lng })
        } catch (err) {}
      }
    } else {
      fitBoundsIfPossible(mapRef.current, mapsRef.current)
    }
  }, [selectedSite])
  const handleRowClick = (rowData) => {
    setHasAutoFitted(true)
    setSelectedSite(rowData)
    firstFitDoneRef.current = true
    if (mapRef.current) {
      mapRef.current.panTo({
        lat: parseFloat(rowData.latitude),
        lng: parseFloat(rowData.longitude),
      })
    }
  }
  useEffect(() => {
    if (topSites?.data?.length > 0 && !selectedSite) {
      setSelectedSite(topSites.data[0])
    }
  }, [topSites, selectedSite])
  useEffect(() => {
    if (!mapRef.current || validSites.length > 0) return

    mapRef.current.setCenter(defaultCenter)
    mapRef.current.setZoom(selectedCountry === 'Jordan' ? 11 : 10)
  }, [selectedCountry])

  useEffect(() => {
    if (!mapRef.current || !mapsRef.current || !validSites.length) return

    if (!hasAutoFitted) {
      const bounds = new mapsRef.current.LatLngBounds()
      validSites.forEach((site) => {
        bounds.extend({
          lat: parseFloat(site.latitude),
          lng: parseFloat(site.longitude),
        })
      })
      mapRef.current.fitBounds(bounds)
    } else if (selectedSite) {
      const lat = parseFloat(selectedSite.latitude)
      const lng = parseFloat(selectedSite.longitude)

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        mapRef.current.panTo({ lat, lng })
      }
    }
  }, [selectedSite, hasAutoFitted, validSites])

  const orderedSites = [...validSites].sort((a, b) => {
    const aSelected = selectedSite && a.stationName === selectedSite.stationName
    const bSelected = selectedSite && b.stationName === selectedSite.stationName

    if (aSelected && !bSelected) return 1
    if (!aSelected && bSelected) return -1
    return 0
  })
  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      {fetchTopSitesRequestState === RequestState.LOADING ? (
        <div className={Styles.Location}>
          <div className={Styles.LocationTextDiv}>
            <IFSkeleton variant="rectangular" width={20} height={20} />
            <IFSkeleton
              className={Styles.LocationText}
              variant="text"
              width={120}
              height={16}
            />
          </div>
          <div className={Styles.Map}>
            <IFSkeleton variant="rectangular" width={'100%'} height={'100%'} />
          </div>
        </div>
      ) : (
        <div className={Styles.Location}>
          <div className={Styles.LocationTextDiv}>
            <IFsvg.Distance />
            <IFText
              className={Styles.LocationText}
              style={{ color: Colors.primary }}
            >
              {selectedSite && topSites?.data?.length > 0
                ? `${selectedSite.stationName}, ${selectedSite.streetName}, ${selectedSite.city}`
                : '...'}
            </IFText>
          </div>
          <div className={Styles.Map}>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: Config.GOOGLE_MAPS_KEY,
                libraries: ['visualization'],
              }}
              defaultZoom={10}
              defaultCenter={defaultCenter}
              options={mapOptions}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => {
                mapRef.current = map
                mapsRef.current = maps

                if (validSites.length > 0) {
                  const bounds = new maps.LatLngBounds()
                  validSites.forEach((site) => {
                    bounds.extend({
                      lat: parseFloat(site.latitude),
                      lng: parseFloat(site.longitude),
                    })
                  })
                  map.fitBounds(bounds)
                  firstFitDoneRef.current = true
                } else {
                  map.setCenter(defaultCenter)
                  map.setZoom(selectedCountry === 'Jordan' ? 11 : 10)
                }
              }}
            >
              {orderedSites.map((site, idx) => {
                const isSelected =
                  selectedSite && selectedSite.stationName === site.stationName

                return (
                  <Marker
                    key={idx}
                    lat={parseFloat(site.latitude)}
                    lng={parseFloat(site.longitude)}
                    onClick={() => {
                      setHasAutoFitted(true)
                      setSelectedSite(site)

                      if (mapRef.current) {
                        mapRef.current.panTo({
                          lat: parseFloat(site.latitude),
                          lng: parseFloat(site.longitude),
                        })
                      }

                      tableRef.current.scrollToRow(
                        topSites.data.findIndex(
                          (s) => s.stationName === site.stationName,
                        ),
                      )
                      tableRef.current.setSelectedRowId(
                        topSites.data.findIndex(
                          (s) => s.stationName === site.stationName,
                        ),
                      )
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        zIndex: isSelected ? 9999 : 1,
                      }}
                      className={Styles.Pins}
                    >
                      <IFsvg.LocationOn
                        width={30}
                        height={30}
                        fill={
                          isSelected ? Colors.primary : Colors.Analytics.Icons
                        }
                      />
                    </div>
                  </Marker>
                )
              })}
            </GoogleMapReact>
          </div>
        </div>
      )}
      <div
        style={{
          border: `1px solid ${Colors.ScheduleChartTick}`,
        }}
        className={Styles.Table}
      >
        <IFTable
          columns={TopSitesTableColumns}
          ref={tableRef}
          tableMaxHeight={379}
          filters={filter}
          tableRequestState={fetchTopSitesRequestState}
          fetchMoreOnBottomReached={fetchMoreOnBottomReached}
          data={topSites.data}
          onRowClick={handleRowClick}
          showSkeleton={
            fetchTopSitesRequestState === RequestState.LOADING ||
            (fetchTopSitesRequestState !== RequestState.SUCCEEDED &&
              topSites.data.length === 0)
          }
          higlightOnClick={true}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  topSites: AnalyticsSelectors.getTopSites(state),
  fetchTopSitesRequestState:
    AnalyticsSelectors.getFetchTopSitesRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchTopSites: (filter) => dispatch(AnalyticsActions.fetchTopSites(filter)),
})

TopSitesCard.propTypes = {
  filter: PropTypes.object,
  topSites: PropTypes.object,
  fetchTopSites: PropTypes.func,
  fetchTopSitesRequestState: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(TopSitesCard)
